"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProfileRoutes = exports.ProfileSubRoutes = void 0;
exports.getProfileUrl = getProfileUrl;
exports.getAbsoluteProfileUrl = getAbsoluteProfileUrl;
exports.getOrganizationProfileUrlForEnterpriseOrganization = getOrganizationProfileUrlForEnterpriseOrganization;
exports.getOrganizationProfileUrlForNonEnterpriseOrganization = getOrganizationProfileUrlForNonEnterpriseOrganization;
exports.getEnterpriseProfileUrl = getEnterpriseProfileUrl;
exports.getUserProfileUrl = getUserProfileUrl;
exports.getUserOrNonEnterpriseOrganizationProfileUrlFromHandle = getUserOrNonEnterpriseOrganizationProfileUrlFromHandle;
exports.getEnterpriseProfileUrlFromHandle = getEnterpriseProfileUrlFromHandle;
exports.getEnterpriseOrganizationProfileUrlFromHandle = getEnterpriseOrganizationProfileUrlFromHandle;
exports.getProfileUrlForAccountHandles = getProfileUrlForAccountHandles;
exports.getProfileUrlForDocumentOwner = getProfileUrlForDocumentOwner;
exports.getAbsoluteProfileUrlForDocumentOwner = getAbsoluteProfileUrlForDocumentOwner;
exports.getProfileUrlForAccountWithDetails = getProfileUrlForAccountWithDetails;
exports.getAccountProfileUrlFromHandle = getAccountProfileUrlFromHandle;
exports.getAccountProfileUrl = getAccountProfileUrl;
exports.profileSettingsRoute = profileSettingsRoute;
exports.userUpgradeRoute = userUpgradeRoute;
exports.getOrganizationProfileUrlFromHandle = getOrganizationProfileUrlFromHandle;
const models_1 = require("@buildwithflux/models");
const error_1 = require("./error");
const origin_1 = require("./origin");
const path_1 = require("./path");
/*
 * =========== Forward routing: ===========
 */
const ProfileBaseRoutes = ["/(enterprise)?/:handle", "/enterprise/:handle/-/:enterpriseOrganizationHandle"];
exports.ProfileSubRoutes = [
    "api-keys",
    "billing",
    "members",
    "settings",
    "starred",
    "upgrade",
    "plan",
    "knowledge",
    "shared",
];
/**
 * A list of all possible profile routes
 *
 * The `handle` parameter is an enterprise handle  if the route params have an optional `0` parameter with value `enterprise`,
 * or if they have a parameter named `enterpriseOrganizationHandle`.
 */
exports.ProfileRoutes = [
    ...ProfileBaseRoutes,
    ...ProfileBaseRoutes.flatMap((baseRoute) => exports.ProfileSubRoutes.map((subRoute) => (0, path_1.joinPathParts)(baseRoute, subRoute))),
];
function getProfileUrl(account, enterpriseOfOrganizationOrOptions, options = {}) {
    // Argument handling
    const enterprise = (0, models_1.accountIsEnterprise)(account)
        ? account
        : enterpriseOfOrganizationOrOptions && "uid" in enterpriseOfOrganizationOrOptions
            ? enterpriseOfOrganizationOrOptions
            : undefined;
    const organization = (0, models_1.accountIsOrganization)(account) ? account : undefined;
    const user = (0, models_1.accountIsUser)(account) ? account : undefined;
    const optionsArg = enterpriseOfOrganizationOrOptions && !("uid" in enterpriseOfOrganizationOrOptions)
        ? enterpriseOfOrganizationOrOptions
        : options;
    if (organization) {
        if ((0, models_1.isEnterpriseOrganization)(organization)) {
            if (!enterprise) {
                throw new error_1.ReverseUrlRoutingError("Invalid arguments to getProfileUrl: enterprise organization without an enterprise passed");
            }
            if (enterprise.uid !== organization.enterpriseUid) {
                throw new error_1.ReverseUrlRoutingError("Invalid arguments to getProfileUrl: enterprise organization does not match enterprise passed");
            }
            return getOrganizationProfileUrlForEnterpriseOrganization(organization, enterprise, optionsArg);
        }
        else if ((0, models_1.isNonEnterpriseOrganization)(organization)) {
            if (enterprise) {
                throw new error_1.ReverseUrlRoutingError("Invalid arguments to getProfileUrl: non-enterprise organization with an enterprise passed");
            }
            return getOrganizationProfileUrlForNonEnterpriseOrganization(organization, optionsArg);
        }
        else {
            throw new error_1.ReverseUrlRoutingError("Invalid organization type: not enterprise nor non-enterprise organization found");
        }
    }
    else if (user) {
        if (enterprise) {
            throw new error_1.ReverseUrlRoutingError("Invalid arguments to getProfileUrl: user does not have an enterprise directly");
        }
        return getUserProfileUrl(user, optionsArg);
    }
    else if (enterprise) {
        return getEnterpriseProfileUrl(enterprise, optionsArg);
    }
    else {
        throw new error_1.ReverseUrlRoutingError("Invalid arguments to getProfileUrl: no user, organization nor enterprise found");
    }
}
function getAbsoluteProfileUrl(account, enterpriseOfOrganizationOrOptions, options = {}) {
    // Argument handling
    const enterprise = (0, models_1.accountIsEnterprise)(account)
        ? account
        : enterpriseOfOrganizationOrOptions && "uid" in enterpriseOfOrganizationOrOptions
            ? enterpriseOfOrganizationOrOptions
            : undefined;
    const organization = (0, models_1.accountIsOrganization)(account) ? account : undefined;
    const user = (0, models_1.accountIsUser)(account) ? account : undefined;
    const optionsArg = !enterpriseOfOrganizationOrOptions || !("uid" in enterpriseOfOrganizationOrOptions)
        ? enterpriseOfOrganizationOrOptions
        : options;
    if (organization) {
        if (enterprise) {
            return (0, origin_1.toAbsoluteUrl)(getProfileUrl(organization, enterprise, optionsArg));
        }
        else {
            return (0, origin_1.toAbsoluteUrl)(getProfileUrl(organization, optionsArg));
        }
    }
    else if (user) {
        return (0, origin_1.toAbsoluteUrl)(getProfileUrl(user, optionsArg));
    }
    else if (enterprise) {
        return (0, origin_1.toAbsoluteUrl)(getProfileUrl(enterprise, optionsArg));
    }
    else {
        throw new error_1.ReverseUrlRoutingError("Invalid account type passed to getAbsoluteProfileUrl");
    }
}
// ---- Constituent functions of the above two functions
function getOrganizationProfileUrlForEnterpriseOrganization(organization, enterprise, options = {}) {
    if (organization.uid === enterprise.defaultOrganizationUid) {
        return getEnterpriseProfileUrl(enterprise, options);
    }
    return ((0, path_1.joinPathParts)("/", "enterprise", enterprise.handle.toLowerCase(), "-", organization.handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options));
}
function getOrganizationProfileUrlForNonEnterpriseOrganization(organization, options = {}) {
    return (0, path_1.joinPathParts)("/", organization.handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}
function getEnterpriseProfileUrl(enterprise, options = {}) {
    return ((0, path_1.joinPathParts)("/enterprise", enterprise.handle.toLowerCase(), options.subRoute) +
        getUrlParamsForProfileUrl(options));
}
function getUserProfileUrl(user, options = {}) {
    return (0, path_1.joinPathParts)(`/`, user.handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}
function getUrlParamsForProfileUrl(options) {
    if (!options.urlParams || !options.urlParams.paymentSuccess) {
        // When more params are added, check for none being set
        return "";
    }
    const searchParams = new URLSearchParams();
    if (options.urlParams.paymentSuccess) {
        searchParams.set("paymentSuccess", "true");
    }
    return `?${searchParams.toString()}`;
}
// ----- Other useful functions for special situations
/*
 * In some cases, such as when trying to route to a user profile from a user handle, or from an IDocumentData object,
 * we need to directly use the handles -- these low-level functions should not be exported, and we should instead export
 * functions for higher-level specific use-cases, such as getProfileUrlForDocumentOwner()
 */
function getUserOrNonEnterpriseOrganizationProfileUrlFromHandle(handle, options = {}) {
    return (0, path_1.joinPathParts)("/", handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}
function getEnterpriseProfileUrlFromHandle(enterpriseHandle, options = {}) {
    return ((0, path_1.joinPathParts)("/enterprise", enterpriseHandle.toLowerCase(), options.subRoute) +
        getUrlParamsForProfileUrl(options));
}
function getEnterpriseOrganizationProfileUrlFromHandle(enterpriseHandle, organizationHandle, options = {}) {
    return ((0, path_1.joinPathParts)("/enterprise", enterpriseHandle.toLowerCase(), "-", organizationHandle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options));
}
function getProfileUrlForAccountHandles(props) {
    if (props.enterpriseHandle) {
        if (props.enterpriseOrganizationHandle) {
            return getEnterpriseOrganizationProfileUrlFromHandle(props.enterpriseHandle, props.enterpriseOrganizationHandle);
        }
        return getEnterpriseProfileUrlFromHandle(props.enterpriseHandle);
    }
    if (props.accountHandle) {
        return getUserOrNonEnterpriseOrganizationProfileUrlFromHandle(props.accountHandle);
    }
    throw new Error("Expected either enterpriseHandle or accountHandle to be provided");
}
function getProfileUrlForDocumentOwner(document, options = {}) {
    if (document.enterprise_owner_uid) {
        if (!document.enterprise_owner_handle) {
            throw new error_1.ReverseUrlRoutingError("Enterprise-owned project must have a enterprise_owner_handle set");
        }
        if (document.organization_owner_is_enterprise_default) {
            return getEnterpriseProfileUrlFromHandle(document.enterprise_owner_handle, options);
        }
        else {
            return getEnterpriseOrganizationProfileUrlFromHandle(document.enterprise_owner_handle, models_1.OrganizationHandle.parse(document.owner_handle), options);
        }
    }
    return getUserOrNonEnterpriseOrganizationProfileUrlFromHandle(document.owner_handle, options);
}
function getAbsoluteProfileUrlForDocumentOwner(document, options = {}) {
    return (0, origin_1.toAbsoluteUrl)(getProfileUrlForDocumentOwner(document, options));
}
function getProfileUrlForAccountWithDetails(account, options) {
    if ((0, models_1.accountIsEnterprise)(account)) {
        return getProfileUrl(account, options);
    }
    else if ((0, models_1.accountIsOrganization)(account)) {
        if ((0, models_1.isEnterpriseOrganization)(account)) {
            return getProfileUrl(account, account.enterprise, options);
        }
        else {
            return getProfileUrl(account, options);
        }
    }
    else {
        return getProfileUrl(account, options);
    }
}
/**
 * @deprecated Can't route correctly for organizations that are part of an enterprise - try something like getProfileUrlForAccountHandles instead
 */
function getAccountProfileUrlFromHandle(handle, options = {}) {
    return (0, path_1.joinPathParts)("/", handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}
/**
 * @deprecated Can't route correctly for organizations that are part of an enterprise
 */
function getAccountProfileUrl(account) {
    return getAccountProfileUrlFromHandle(account.handle);
}
/**
 * @deprecated Doesn't produce correct route for an organization belonging to an enterprise
 */
function profileSettingsRoute(handle, options = { paymentSuccess: false }) {
    if (options?.paymentSuccess) {
        return `/${handle}/settings?paymentSuccess=true`;
    }
    return `/${handle}/settings`;
}
/**
 * @deprecated Doesn't produce correct route for an organization belonging to an enterprise
 */
function userUpgradeRoute(handle) {
    return `/${handle}/upgrade`;
}
/**
 * @deprecated not valid for reverse-routing an organization that belongs to an enterprise
 */
function getOrganizationProfileUrlFromHandle(handle) {
    return (0, path_1.joinPathParts)("/", handle.toLowerCase());
}
//# sourceMappingURL=profile.js.map